"use client";

import { proxy } from "valtio";
import {
  getLatestSeen,
  setLatestSeen,
  shouldShowNotificationDot,
  type NotificationBellHead,
} from "./localNotificationState";
import log from "logbeacon";

interface ChatBellSharedState {
  ownerId?: string;
  latestHead: NotificationBellHead | null;
  showDot: boolean;
  isLoading: boolean;
}

let acknowledgeTimer: ReturnType<typeof setTimeout> | null = null;

const chatBellSharedState = proxy<ChatBellSharedState>({
  ownerId: undefined,
  latestHead: null,
  showDot: false,
  isLoading: false,
});

function clearAcknowledgeTimer(): void {
  if (acknowledgeTimer) {
    clearTimeout(acknowledgeTimer);
    acknowledgeTimer = null;
  }
}

function ensureOwnerScope(ownerId?: string): void {
  if (!ownerId) {
    clearAcknowledgeTimer();
    chatBellSharedState.ownerId = undefined;
    chatBellSharedState.latestHead = null;
    chatBellSharedState.showDot = false;
    chatBellSharedState.isLoading = false;
    return;
  }

  if (chatBellSharedState.ownerId !== ownerId) {
    clearAcknowledgeTimer();
    chatBellSharedState.ownerId = ownerId;
    chatBellSharedState.latestHead = null;
    chatBellSharedState.showDot = false;
    chatBellSharedState.isLoading = false;
  }
}

export function resetChatBellState(ownerId?: string): void {
  ensureOwnerScope(ownerId);
}

export function setChatBellLoading(ownerId: string, loading: boolean): void {
  ensureOwnerScope(ownerId);
  chatBellSharedState.isLoading = loading;
}

export function updateChatBellHead(
  ownerId: string,
  latestHead: NotificationBellHead | null,
): void {
  ensureOwnerScope(ownerId);
  chatBellSharedState.latestHead = latestHead;
  const latestSeen = getLatestSeen(ownerId);
  chatBellSharedState.showDot = shouldShowNotificationDot(
    latestHead,
    latestSeen,
  );
}

export function acknowledgeChatBell(ownerId?: string): void {
  if (!ownerId) {
    return;
  }
  ensureOwnerScope(ownerId);

  const latestHead = chatBellSharedState.latestHead;
  if (!latestHead) {
    return;
  }

  setLatestSeen(ownerId, latestHead);
  clearAcknowledgeTimer();
  acknowledgeTimer = setTimeout(() => {
    if (chatBellSharedState.ownerId !== ownerId) {
      return;
    }
    const latestSeen = getLatestSeen(ownerId);
    chatBellSharedState.showDot = shouldShowNotificationDot(
      chatBellSharedState.latestHead,
      latestSeen,
    );
    log.trace("[GTM_VIEW][bell_red_dot_clear]");
  }, 5000);
}

export { chatBellSharedState };
